import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { buildCumulativeData } from '../lib/buildCostControlData'
import { computeConcentrationRisk } from '../lib/commercialAnalytics'

// ─── Formatters ──────────────────────────────────────────────────────────
const fmt  = n => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })
const fmtM = n => {
  const v = Math.abs(Number(n || 0))
  if (v >= 1e9) return (Number(n) / 1e9).toFixed(2) + 'B'
  if (v >= 1e6) return (Number(n) / 1e6).toFixed(2) + 'M'
  if (v >= 1e3) return (Number(n) / 1e3).toFixed(1) + 'K'
  return fmt(n)
}

// ─── Fetch helper for potentially large raw datasets ───────────────────
async function fetchAllRows(queryFn) {
  const PAGE = 1000
  let from = 0
  let all = []
  while (true) {
    const { data } = await queryFn({ from, to: from + PAGE - 1 })
    if (!data?.length) break
    all = all.concat(data)
    if (data.length < PAGE) break
    from += PAGE
  }
  return all
}

// ─── Severity presentation ──────────────────────────────────────────────
const SEV = {
  critical: { label: 'CRITICAL', color: '#DC2626', bg: '#FEF2F2', border: '#FECACA', badge: '#DC2626' },
  warning:  { label: 'WARNING',  color: '#B45309', bg: '#FFFBEB', border: '#FDE68A', badge: '#D97706' },
  watch:    { label: 'WATCH',    color: '#1D4ED8', bg: '#EFF6FF', border: '#BFDBFE', badge: '#2563EB' },
}
const SEV_ORDER = { critical: 0, warning: 1, watch: 2 }

function formatImpact(impact) {
  if (impact.format === 'currency') return `SAR ${fmt(impact.value)}`
  if (impact.format === 'percentage') return `${impact.value.toFixed(1)}%`
  return `${impact.value} days`
}
function formatImpactCompact(impact) {
  if (impact.format === 'currency') return `SAR ${fmtM(impact.value)}`
  if (impact.format === 'percentage') return `${impact.value.toFixed(1)}%`
  return `${impact.value} days`
}

function SeverityBadge({ level }) {
  const s = SEV[level] || SEV.watch
  return <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 9px', borderRadius: 20, background: s.badge, color: '#fff' }}>{s.label}</span>
}

// ═════════════════════════════════════════════════════════════════════════
// Exception evaluation — each rule is independent and atomic. No rule
// consumes another rule's output. No composite/correlated exceptions.
// ═════════════════════════════════════════════════════════════════════════

function evaluateBudgetOverCV(snapshot, project) {
  const totalBudget = snapshot.grandTotals.bac || 0
  const contractValue = parseFloat(project.contract_value) || 0
  if (!(totalBudget > contractValue)) return []

  return [{
    id: `budget-over-cv:${project.id}`,
    type: 'budget_over_cv',
    domain: 'Financial',
    severity: 'critical',
    title: 'Budget Exceeds Contract Value',
    entity_type: null,
    entity_id: null,
    entity_reference: null,
    age_days: null,
    impact: {
      label: 'Budget Overrun',
      value: totalBudget - contractValue,
      format: 'currency',
    },
    evidence: [
      { label: 'Total Budget', value: fmt(totalBudget) },
      { label: 'Contract Value', value: fmt(contractValue) },
      { label: 'Direct Budget', value: fmt(snapshot.directTotals.bac || 0) },
      { label: 'Indirect Budget', value: fmt(snapshot.indirectTotals.bac || 0) },
    ],
    explanation: 'Total project budget exceeds the contract value.',
    disclaimer: null,
    source_navigation: { screen: 'costcontrol' },
  }]
}

function evaluateConcentrationRisk(subcontracts, equipmentContracts, project) {
  const { concentrationPct, totalActiveValue, activeContractsCount, largestContract } =
    computeConcentrationRisk(subcontracts, equipmentContracts)

  if (!largestContract || !(concentrationPct > 10)) return []

  const severity = concentrationPct > 20 ? 'warning' : 'watch'

  return [{
    id: `concentration-risk:${project.id}`,
    type: 'concentration_risk',
    domain: 'Commercial',
    severity,
    title: 'Subcontractor Concentration Risk',
    entity_type: null,
    entity_id: null,
    entity_reference: null,
    age_days: null,
    impact: {
      label: 'Concentration Exposure',
      value: concentrationPct,
      format: 'percentage',
    },
    evidence: [
      { label: 'Contractor', value: largestContract.contractor_name || '—' },
      { label: 'Contract Number', value: largestContract.contract_no || '—' },
      { label: 'Contract Type', value: largestContract.contract_type === 'equipment' ? 'Equipment' : 'Subcontract' },
      { label: 'Contract Value', value: fmt(largestContract.contract_value) },
      { label: 'Total Active Contract Value', value: fmt(totalActiveValue) },
      { label: 'Active Contracts Count', value: activeContractsCount },
    ],
    explanation: `${largestContract.contractor_name || 'This contractor'} holds ${concentrationPct.toFixed(1)}% of total active contract value.`,
    disclaimer: null,
    source_navigation: { screen: 'subcontracts' },
  }]
}

function evaluatePOsWithoutReceipt(pos, receipts, today) {
  const receivedPOIds = new Set((receipts || []).map(r => r.po_id).filter(Boolean))

  return (pos || [])
    .filter(po => po.po_type !== 'service')
    .filter(po => !receivedPOIds.has(po.id))
    .filter(po => po.issue_date)
    .map(po => ({ po, ageDays: Math.floor((today - new Date(po.issue_date)) / 86400000) }))
    .filter(({ ageDays }) => ageDays > 30)
    .map(({ po, ageDays }) => ({
      id: `po-no-receipt:${po.id}`,
      type: 'po_no_receipt',
      domain: 'Operational',
      severity: 'warning',
      title: 'Purchase Order Without Receipt',
      entity_type: 'purchase_order',
      entity_id: po.id,
      entity_reference: po.po_number,
      age_days: ageDays,
      impact: {
        label: 'Committed Value at Risk',
        value: parseFloat(po.total_exc_vat) || 0,
        format: 'currency',
      },
      evidence: [
        { label: 'PO Number', value: po.po_number },
        { label: 'Vendor', value: po.vendor || '—' },
        { label: 'PO Type', value: po.po_type || '—' },
        { label: 'Issue Date', value: po.issue_date },
        { label: 'Status', value: po.status },
        { label: 'Total Value', value: fmt(po.total_exc_vat) },
        { label: 'Store Receipts', value: 'No receipts found in Store for this PO' },
      ],
      explanation: `Purchase Order ${po.po_number} was approved ${ageDays} days ago but has no recorded receipt in Store.`,
      disclaimer: null,
      source_navigation: { screen: 'po-reconciliation' },
    }))
}

function evaluateIPCAging(ipcs, subcontracts, equipmentContracts, today) {
  const subByid = {}
  ;(subcontracts || []).forEach(s => { subByid[s.id] = s })
  const eqByid = {}
  ;(equipmentContracts || []).forEach(e => { eqByid[e.id] = e })

  return (ipcs || [])
    .filter(c => c.period_to)
    .map(c => ({ c, ageDays: Math.floor((today - new Date(c.period_to)) / 86400000) }))
    .filter(({ ageDays }) => ageDays > 30)
    .map(({ c, ageDays }) => {
      const isEquipment = !!c.equipment_contract_id
      const contract = isEquipment ? eqByid[c.equipment_contract_id] : subByid[c.subcontract_id]
      const contractLabel = contract ? `${contract.contract_no || '—'} — ${contract.contractor_name || '—'}` : '—'

      return {
        id: `ipc-aging:${c.id}`,
        type: 'ipc_aging',
        domain: 'Operational',
        severity: 'warning',
        title: 'Approved IPC Aging Past Terms',
        entity_type: 'ipc',
        entity_id: c.id,
        entity_reference: c.ipc_number,
        age_days: ageDays,
        impact: {
          label: 'Net Payable Exposure',
          value: parseFloat(c.net_payable) || 0,
          format: 'currency',
        },
        evidence: [
          { label: 'IPC Number', value: c.ipc_number },
          { label: 'Period', value: `${c.period_from || '—'} → ${c.period_to}` },
          { label: 'Status', value: c.status },
          { label: 'Gross Amount', value: fmt(c.gross_amount) },
          { label: 'Net Payable', value: fmt(c.net_payable) },
          { label: 'Days Since Period End', value: ageDays },
          { label: 'Contract', value: contractLabel },
          { label: 'Contract Type', value: isEquipment ? 'Equipment' : 'Subcontract' },
        ],
        explanation: `IPC ${c.ipc_number} was approved and is ${ageDays} days past its period end, exceeding standard payment terms. This is an aging indicator, not a confirmed payment status.`,
        disclaimer: 'This exception is based on elapsed time since period end, not confirmed payment records. It may remain flagged even if payment has since been made elsewhere in the system.',
        source_navigation: { screen: 'ipc' },
      }
    })
}

function evaluateStalledPRs(prs, rfqLinks, prType, today) {
  const linkedIds = new Set((rfqLinks || []).map(r => r.pr_id).filter(Boolean))

  return (prs || [])
    .filter(pr => !linkedIds.has(pr.id))
    .filter(pr => pr.created_at)
    .map(pr => ({ pr, ageDays: Math.floor((today - new Date(pr.created_at)) / 86400000) }))
    .filter(({ ageDays }) => ageDays > 14)
    .map(({ pr, ageDays }) => ({
      id: `stalled-pr:${prType}:${pr.id}`,
      type: 'pr_stalled',
      domain: 'Operational',
      severity: 'watch',
      title: 'Stalled Purchase Requisition',
      entity_type: 'purchase_requisition',
      entity_id: pr.id,
      entity_reference: pr.pr_number,
      age_days: ageDays,
      impact: {
        label: 'Time Exposure',
        value: ageDays,
        format: 'days',
      },
      evidence: [
        { label: 'PR Number', value: pr.pr_number },
        { label: 'Description', value: pr.description || '—' },
        { label: 'Approval Stage', value: pr.approval_stage },
        { label: 'Created At', value: pr.created_at },
        { label: 'Days Since Creation', value: ageDays },
        { label: 'PR Type', value: prType === 'equipment' ? 'Equipment' : 'Regular' },
        { label: 'RFQ Status', value: 'No RFQ has been initiated for this PR' },
      ],
      explanation: `Purchase Requisition ${pr.pr_number} was approved but has no linked RFQ, ${ageDays} days since creation.`,
      disclaimer: 'Age is measured from PR creation date, not from approval date. A PR that spent time in draft before approval may appear older than its actual time since approval.',
      source_navigation: { screen: 'purchase-requisitions' },
    }))
}

function sortExceptions(list) {
  return [...list].sort((a, b) => {
    const sevDiff = SEV_ORDER[a.severity] - SEV_ORDER[b.severity]
    if (sevDiff !== 0) return sevDiff
    if (a.impact.format === 'currency' || a.impact.format === 'percentage') return b.impact.value - a.impact.value
    return (b.age_days || 0) - (a.age_days || 0)
  })
}

// ═════════════════════════════════════════════════════════════════════════
// Presentation components
// ═════════════════════════════════════════════════════════════════════════

function ExposureSummary({ exceptions }) {
  const critical = exceptions.filter(e => e.severity === 'critical').length
  const warning = exceptions.filter(e => e.severity === 'warning').length
  const watch = exceptions.filter(e => e.severity === 'watch').length
  const totalExposure = exceptions
    .filter(e => e.impact.format === 'currency')
    .reduce((s, e) => s + e.impact.value, 0)

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 12, marginBottom: 20 }}>
      <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: '14px 16px' }}>
        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: '#9CA3AF', marginBottom: 6 }}>Critical</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#DC2626' }}>{critical}</div>
      </div>
      <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: '14px 16px' }}>
        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: '#9CA3AF', marginBottom: 6 }}>Warning</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#D97706' }}>{warning}</div>
      </div>
      <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: '14px 16px' }}>
        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: '#9CA3AF', marginBottom: 6 }}>Watch</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#2563EB' }}>{watch}</div>
      </div>
      <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: '14px 16px' }}>
        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: '#9CA3AF', marginBottom: 6 }}>Total Financial Exposure</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#0F1017' }}>SAR {fmtM(totalExposure)}</div>
      </div>
    </div>
  )
}

function ExceptionRow({ exception, onOpen }) {
  const s = SEV[exception.severity] || SEV.watch
  return (
    <div
      onClick={() => onOpen(exception)}
      style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 8, marginBottom: 8, padding: '12px 14px', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 12 }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: '#0F1017' }}>{exception.title}</span>
          <SeverityBadge level={exception.severity} />
          {exception.entity_reference && (
            <span style={{ fontSize: 10.5, color: '#6B7280' }}>{exception.entity_reference}</span>
          )}
        </div>
        <p style={{ fontSize: 11.5, color: '#4B5563', lineHeight: 1.4 }}>{exception.explanation}</p>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: s.color, fontVariantNumeric: 'tabular-nums' }}>
          {formatImpactCompact(exception.impact)}
        </div>
        <div style={{ fontSize: 9.5, color: '#9CA3AF', marginTop: 2 }}>{exception.impact.label}</div>
      </div>
    </div>
  )
}

function DetailDrawer({ exception, onClose, onNavigate }) {
  if (!exception) return null
  const s = SEV[exception.severity] || SEV.watch
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', display: 'flex', justifyContent: 'flex-end', zIndex: 50 }} onClick={onClose}>
      <div style={{ width: 440, maxWidth: '92vw', background: '#fff', height: '100%', overflowY: 'auto', padding: '22px 22px 100px' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
          <div>
            <SeverityBadge level={exception.severity} />
            <h2 style={{ fontSize: 16, fontWeight: 800, color: '#0F1017', marginTop: 8 }}>{exception.title}</h2>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, color: '#9CA3AF', cursor: 'pointer' }}>×</button>
        </div>

        <p style={{ fontSize: 12.5, color: '#374151', lineHeight: 1.5, marginBottom: 16 }}>{exception.explanation}</p>

        <div style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', color: '#6B7280', marginBottom: 4 }}>{exception.impact.label}</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>
            {formatImpact(exception.impact)}
          </div>
        </div>

        <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', color: '#9CA3AF', marginBottom: 8 }}>Evidence</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5, marginBottom: 16 }}>
          <tbody>
            {exception.evidence.map((row, i) => (
              <tr key={i} style={{ borderTop: '1px solid #F3F4F6' }}>
                <td style={{ padding: '6px 0', color: '#6B7280', width: '42%' }}>{row.label}</td>
                <td style={{ padding: '6px 0', color: '#0F1017', fontWeight: 600 }}>{row.value}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {exception.disclaimer && (
          <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: '10px 12px', marginBottom: 16 }}>
            <div style={{ fontSize: 10.5, color: '#6B7280', lineHeight: 1.5 }}>{exception.disclaimer}</div>
          </div>
        )}

        <button
          onClick={() => onNavigate(exception.source_navigation.screen)}
          style={{ width: '100%', background: '#1B3A6B', color: '#fff', border: 'none', borderRadius: 8, padding: '11px 0', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
        >
          Open in source screen →
        </button>
      </div>
    </div>
  )
}

// ═════════════════════════════════════════════════════════════════════════
// Main component
// ═════════════════════════════════════════════════════════════════════════

export default function AlertsVariances({ initProject, onNavigate }) {
  const [projects, setProjects] = useState([])
  const [selectedProject, setSelectedProject] = useState(null)
  const [exceptions, setExceptions] = useState(null)
  const [loading, setLoading] = useState(false)
  const [domainFilter, setDomainFilter] = useState('all')
  const [severityFilter, setSeverityFilter] = useState('all')
  const [activeException, setActiveException] = useState(null)

  useEffect(() => {
    supabase.from('projects').select('id,name,project_no,currency,contract_value,status,organizations(name)').order('name')
      .then(({ data }) => setProjects(data || []))
  }, [])

  useEffect(() => { if (initProject) setSelectedProject(initProject) }, [initProject?.id])
  useEffect(() => { if (selectedProject?.id) loadExceptions(selectedProject) }, [selectedProject?.id]) // eslint-disable-line

  const loadExceptions = useCallback(async (project) => {
    setLoading(true)
    setExceptions(null)
    const pid = project.id
    const today = new Date()

    // ── Financial Snapshot (SSOT) ───────────────────────────────────────
    const snapshot = await buildCumulativeData(pid, today.getMonth() + 1, today.getFullYear())

    // ── Raw domain reads (Operational — no owner exists for these) ─────
    const [
      pos, receipts, ipcs, subcontracts, equipmentContracts,
      prs, rfqLinks, eqPrs, eqRfqLinks,
    ] = await Promise.all([
      fetchAllRows(({ from, to }) =>
        supabase.from('purchase_orders')
          .select('id,po_number,vendor,total_exc_vat,status,issue_date,po_type')
          .eq('project_id', pid).eq('status', 'approved')
          .range(from, to)),
      fetchAllRows(({ from, to }) =>
        supabase.from('store_receipts').select('po_id').eq('project_id', pid).range(from, to)),
      fetchAllRows(({ from, to }) =>
        supabase.from('ipc_certificates')
          .select('id,ipc_number,period_from,period_to,status,net_payable,gross_amount,subcontract_id,equipment_contract_id')
          .eq('project_id', pid).eq('status', 'approved')
          .range(from, to)),
      supabase.from('subcontracts').select('id,contract_no,contractor_name,contract_value,status').eq('project_id', pid).then(r => r.data || []),
      supabase.from('equipment_contracts').select('id,contract_no,contractor_name,contract_value,status').eq('project_id', pid).then(r => r.data || []),
      fetchAllRows(({ from, to }) =>
        supabase.from('purchase_requisitions')
          .select('id,pr_number,description,approval_stage,created_at')
          .eq('project_id', pid).eq('approval_stage', 'approved')
          .range(from, to)),
      fetchAllRows(({ from, to }) =>
        supabase.from('rfq_requests').select('pr_id').eq('project_id', pid).range(from, to)),
      fetchAllRows(({ from, to }) =>
        supabase.from('equipment_prs')
          .select('id,pr_number,description,approval_stage,created_at')
          .eq('project_id', pid).eq('approval_stage', 'approved')
          .range(from, to)),
      fetchAllRows(({ from, to }) =>
        supabase.from('equipment_rfqs').select('pr_id').eq('project_id', pid).range(from, to)),
    ])

    // ── Evaluate the five approved, atomic exception rules ──────────────
    const all = [
      ...evaluateBudgetOverCV(snapshot, project),
      ...evaluateConcentrationRisk(subcontracts, equipmentContracts, project),
      ...evaluatePOsWithoutReceipt(pos, receipts, today),
      ...evaluateIPCAging(ipcs, subcontracts, equipmentContracts, today),
      ...evaluateStalledPRs(prs, rfqLinks, 'regular', today),
      ...evaluateStalledPRs(eqPrs, eqRfqLinks, 'equipment', today),
    ]

    setExceptions(sortExceptions(all))
    setLoading(false)
  }, [])

  // ── Project picker ──────────────────────────────────────────────────
  if (!selectedProject) return (
    <div style={{ minHeight: '100vh', background: '#F3F4F6', fontFamily: 'inherit' }}>
      <div style={{ padding: '20px 24px', maxWidth: 1000, margin: '0 auto' }}>
        <h1 style={{ fontSize: 20, fontWeight: 800, color: '#0F1017', letterSpacing: '-.02em' }}>Risk &amp; Exceptions</h1>
        <p style={{ fontSize: 11.5, color: '#6B7280', marginTop: 3, marginBottom: 20 }}>Project risk and exception workspace</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 12 }}>
          {projects.map(p => (
            <button key={p.id} onClick={() => setSelectedProject(p)}
              style={{ textAlign: 'left', background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: 16, cursor: 'pointer', fontFamily: 'inherit' }}>
              <div style={{ fontWeight: 700, color: '#0F1017', fontSize: 13.5 }}>{p.name}</div>
              <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{p.organizations?.name}{p.project_no ? ` · #${p.project_no}` : ''}</div>
              {p.contract_value && <div style={{ fontSize: 12.5, fontWeight: 700, color: '#1A7A4A', marginTop: 8 }}>SAR {fmtM(parseFloat(p.contract_value))}</div>}
            </button>
          ))}
        </div>
      </div>
    </div>
  )

  const filtered = (exceptions || []).filter(e =>
    (domainFilter === 'all' || e.domain === domainFilter) &&
    (severityFilter === 'all' || e.severity === severityFilter)
  )

  return (
    <div style={{ minHeight: '100vh', background: '#F3F4F6', fontFamily: 'inherit' }}>
      {/* Header / Project Context */}
      <div style={{ background: '#fff', borderBottom: '1px solid #E5E7EB', padding: '18px 24px' }}>
        <h1 style={{ fontSize: 20, fontWeight: 800, color: '#0F1017', letterSpacing: '-.02em' }}>Risk &amp; Exceptions</h1>
        <p style={{ fontSize: 11.5, color: '#6B7280', marginTop: 3 }}>
          {selectedProject.name}
          <button onClick={() => { setSelectedProject(null); setExceptions(null) }}
            style={{ marginLeft: 8, fontSize: 10, color: '#9CA3AF', background: 'none', border: '1px solid #E5E7EB', borderRadius: 4, padding: '1px 7px', cursor: 'pointer' }}>
            × Change
          </button>
        </p>
      </div>

      <div style={{ padding: '20px 24px', maxWidth: 1100, margin: '0 auto' }}>
        {loading && <div style={{ fontSize: 12, color: '#9CA3AF', padding: '40px 0', textAlign: 'center' }}>Loading…</div>}

        {!loading && exceptions && (
          <>
            {/* Exposure Summary */}
            <ExposureSummary exceptions={exceptions} />

            {/* Filters */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              <select value={domainFilter} onChange={e => setDomainFilter(e.target.value)}
                style={{ fontSize: 11.5, border: '1px solid #E5E7EB', borderRadius: 6, padding: '6px 10px', background: '#fff' }}>
                <option value="all">All Domains</option>
                <option value="Financial">Financial</option>
                <option value="Commercial">Commercial</option>
                <option value="Operational">Operational</option>
              </select>
              <select value={severityFilter} onChange={e => setSeverityFilter(e.target.value)}
                style={{ fontSize: 11.5, border: '1px solid #E5E7EB', borderRadius: 6, padding: '6px 10px', background: '#fff' }}>
                <option value="all">All Severities</option>
                <option value="critical">Critical</option>
                <option value="warning">Warning</option>
                <option value="watch">Watch</option>
              </select>
            </div>

            {/* Exception Queue */}
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#9CA3AF', marginBottom: 8 }}>
              Exceptions ({filtered.length})
            </div>
            {filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#9CA3AF', fontSize: 12.5, background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10 }}>
                ✓ No active exceptions
              </div>
            ) : (
              filtered.map(e => <ExceptionRow key={e.id} exception={e} onOpen={setActiveException} />)
            )}
          </>
        )}
      </div>

      {/* Detail Drawer */}
      <DetailDrawer
        exception={activeException}
        onClose={() => setActiveException(null)}
        onNavigate={(screen) => { onNavigate?.(screen); setActiveException(null) }}
      />
    </div>
  )
}
